import moment from 'moment';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { addVenuePhoto, geoCode, getVenuePrivate, saveHostedVenue } from '../../../firebase/venues';
import {
  addPhotos,
  movePhoto,
  getForm,
  getFormErrors,
  getGeoErrors,
  getIsSaving,
  getSaveError,
  getUploadError,
  setFormErrors,
  setFormValue,
  setGeoErrors,
  setIsSaving,
  setPrivateRecord,
  setSaveError,
  setUploadError
} from '../../../store/hosted-venue';
import { getUser } from '../../../store/user';
import { states } from '../../helpers';
import { colors, FieldGroupFullWidth, headingStyles, notMobile, tablet } from '../../styles';

import {
  useHostVenueAddUnavailableTag,
  useHostVenueCancelTag,
  useHostVenueEditTag,
  useHostVenueNavigateAwayTag,
  useHostVenueSaveTag,
  useHostVenueSwitchTag
} from '../../../tag-manager/host';
import { CalendarIcon, CloseIcon, Field, FieldGroup, FormLabel, ImagesIcon, Input, Select, Textarea, VenueIcon, WhiteCloseIcon } from '../../styles';
import { CURRENT_HOST_REQUIREMENTS } from '../../../content/hostRequirements';
import editorCopy from '../../../content/venueEditor';
import { fieldHelp, fromLegacy, listOptions, maxLength, toLegacy, valueLabel } from '../../../taxonomy';
import { clearConfirmation, readConfirmation } from '../../ListYourVenue/confirmation';
import { amenitiesFrom, currencyFor, MIN_PHOTOS, maxGuestsFrom, privateWrite, ruleValues, venueFactsWrite, venueProblems, visibleLength } from './venueFacts';
import { DayHours, HourlyPrice, Label, OnSiteContact, RequirementsBox, VenueFactSections } from './VenueFactFields';
import ExcludeDatesModal from './ExcludeDatesModal';
import SundayTermsModal from './SundayTermsModal';
import { useAllSundaysOpen } from '../../../firebase/settings';
import { formatPhone, HOST_SUNDAY_SWITCH, hostSundayChoice, venueOpensSundays, venueSundaysFlag } from '../../sundays';
import { saveHostSundays, useHostSundays } from '../../../firebase/sundays';
import { StaticPhotos, tileCss } from './PhotoTile';
import { collectSources, entriesFromDrop, readDroppedEntries } from './photoSources';
import {
  createLimiter,
  isUnreadable,
  openSource,
  pickMessage,
  planPick,
  shrinkPhoto,
  takeCommittable,
  unreadableMessage,
  UPLOAD_CONCURRENCY
} from './photoUpload';

// NOTE: if you change these, change on functions/index.js & BookModal.js as well.
const DATE_FORMAT = 'YYYY-MM-DD';
const START_DATE_FORMAT = 'MMM D';
const END_DATE_FORMAT = 'MMM D, YYYY';

const formatIntString = (value) => {
  value = String(value).replace(/[^0-9\.]/g, '');
  if (!value) {
    return '';
  }
  return parseInt(value).toString();
};

const getNextContinuousDate = (group) => {
  if (!group.length) return null;
  const lastDate = group[group.length - 1];
  return moment(`${lastDate}T00:00:00.000`).add(1, 'day').format(DATE_FORMAT);
};
const groupContinuousDates = (dates) => {
  const lastIndex = dates.length - 1;
  const sortedDates = [...dates];
  sortedDates.sort((a, b) => {
    if (a < b) return -1;
    if (b < a) return 1;
    return 0;
  });
  const { groups } = sortedDates.reduce(
    ({ groups, group }, date, i) => {
      // if first & last item
      if (i === 0 && i === lastIndex) {
        return { groups: [[date]] };
      }
      // if first item
      if (i === 0) {
        return { groups, group: [date] };
      }
      // handle if continuous or not
      const nextDayOfLastDate = getNextContinuousDate(group);
      if (date !== nextDayOfLastDate) {
        groups.push(group);
        group = [date];
      } else {
        group.push(date);
      }
      // if last item
      if (i === lastIndex) {
        groups.push(group);
        return { groups };
      }
      return { groups, group };
    },
    { groups: [], group: [] }
  );
  return groups;
};

// Lists stored today as their old labels (until VS2.1). A retired value
// (e.g. "hotel") stays on a venue that has it, but is never offered.
const legacyOptions = (list, current, except = []) => {
  const options = listOptions(list, { except }).map(({ id, label }) => ({ value: toLegacy(list, id), label }));
  if (current && !options.some((option) => option.value === current)) {
    options.push({ value: current, label: valueLabel(list, fromLegacy(list, current)) || current });
  }
  return options;
};

const DESCRIPTION_HELP = fieldHelp('description');

const TooLong = ({ name, id, form, errors }) =>
  errors[name] === 'tooLong' ? <ErrorHint style={{ marginLeft: 0 }}>{editorCopy.tooLong(maxLength(id), visibleLength(form[name]))}</ErrorHint> : null;

const FieldHelp = styled.div`
  font-size: 12px;
  color: ${colors.fadedText};
  margin: -2px 0 6px;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  color: ${colors.defaultText};
`;

const FormTitle = styled.div`
  ${headingStyles}
  font-size: 24px;
  line-height: 1.2;
  margin-bottom: 12px;
  display: flex;
  justify-content: flex-start;
  gap: 8px;

  svg {
    display: inline-block;
    height: 20px;
    width: 20px;
    margin-top: -6px;
  }
`;

const FormTitleText = styled.div`
  flex-shrink: 1;
  flex-grow: 1;
`;

const CloseButtonWrapper = styled.div`
  flex: 0 0 auto;

  @media (min-width: ${notMobile}) {
    display: none;
  }

  button {
    border: none;
    background-color: transparent;
    margin: 0;

    svg {
      height: 18px;
      width: 18px;
    }
  }
`;

const Title = styled.div`
  font-weight: 500;
  font-size: 16px;
  margin: 12px 0;

  &:first-of-type {
    margin-top: 0;
  }
`;

const Photos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: ${notMobile}) {
    grid-template-columns: repeat(auto-fill, 120px);
  }

  & > .empty-photo {
    ${tileCss}
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${colors.lightBackground};

    & > span svg {
      display: block;
      height: 32px;
      width: 32px;
      opacity: 0.3;
    }
  }
`;

const PhotoDropZone = styled.div`
  position: relative;

  & > .drop-hint {
    position: absolute;
    inset: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.9);
    border: 2px dashed ${colors.primaryButton};
    border-radius: 8px;
    color: ${colors.primaryButton};
    font-size: 16px;
    font-weight: 500;
    pointer-events: none;
    z-index: 1;
  }
`;

const UploadTile = styled.div`
  ${tileCss}
  background-color: ${colors.lightBackground};
  border-radius: 8px;

  img {
    opacity: 0.45;
    transition: opacity 0.3s ease-out;
  }

  &.done img {
    opacity: 1;
  }

  & > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 36px;
    height: 36px;
    margin: -18px 0 0 -18px;
    transform: rotate(-90deg);

    circle {
      fill: none;
      stroke-width: 3;
      transition: stroke-dashoffset 0.2s linear;
    }
  }

  & > .tile-error {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.85);
    color: ${colors.errorColor};
    font-size: 12px;
    font-weight: 500;
    text-align: center;

    button {
      padding: 2px 10px;
      font-size: 12px;
      color: ${colors.primaryButton};
      background: white;
      border: 1px solid ${colors.primaryButton};
      border-radius: 4px;
    }
  }
`;

const RING_RADIUS = 15;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
const ProgressRing = ({ progress }) => (
  <svg viewBox="0 0 36 36" aria-hidden="true">
    <circle cx="18" cy="18" r={RING_RADIUS} stroke="rgba(0, 0, 0, 0.15)" />
    <circle
      cx="18"
      cy="18"
      r={RING_RADIUS}
      stroke={colors.primaryButton}
      strokeDasharray={RING_LENGTH}
      strokeDashoffset={RING_LENGTH * (1 - (progress || 0))}
    />
  </svg>
);

const PhotoHint = styled.div`
  margin: -8px 0 16px;
  font-size: 12px;
  color: ${colors.fadedText};

  button {
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font: inherit;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ReorderHint = styled.div`
  margin: -4px 0 12px;
  font-size: 12px;
  color: ${colors.fadedText};
`;

const PhotoNotice = styled.div`
  margin: -8px 0 16px;
  font-size: 12px;
`;

const hasMouse = () => typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches;

// Folder picking works only with a mouse: phone browsers do not support it.
// A browser picker picks files or a folder, never both: "Add Photos" takes photos
// and .zip files; the "choose a folder" link opens the folder picker.
const canPickFolder = () => hasMouse() && 'webkitdirectory' in document.createElement('input');

const SortablePhotos = lazy(() => import('./SortablePhotos'));
const VenueTextEditor = lazy(() => import('./VenueTextEditor'));

// V4: the editing bar loads after the form opens. Until then the plain box works the same way.
const VenueTextField = ({ value, onChange, placeholder, label, minLines }) => (
  <Suspense fallback={<Textarea placeholder={placeholder} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />}>
    <VenueTextEditor value={value} onChange={onChange} placeholder={placeholder} label={label} minLines={minLines} />
  </Suspense>
);

const DESCRIPTION_PLACEHOLDER =
  'Describe the property: the setting, the spaces guests can use, and what makes it one of a kind. Bold a line to make it a heading.';
const NOTES_PLACEHOLDER = 'Optional. House rules, parking, setup times, and anything else bookers should know.';

const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');

const uploadPhoto = (file, onProgress) =>
  new Promise((resolve, reject) =>
    addVenuePhoto({ photo: file, onProgress }, (photo) => (photo && photo.ref && photo.url ? resolve(photo) : reject()), reject)
  );

const FormButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 16px;

  button + button {
    margin-left: 16px;
  }
`;

const InputButton = styled.button`
  font-weight: 500;
  color: white;
  background-color: ${colors.primaryButton};
  font-size: 14px;
  border-radius: 4px;
  padding: 8px 16px;
  border: none;
  width: max-content;

  ${({ ghostStyle }) =>
    ghostStyle &&
    css`
      color: ${colors.primaryButton};
      background-color: transparent;
      border: 1px solid ${colors.primaryButton};
    `}

  ${({ hasError }) =>
    hasError &&
    css`
      background-color: ${colors.errorColor};
    `}

  @media (max-width: ${tablet}) {
    padding: 12px 33px;
  }

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const TextButton = styled.button`
  font-weight: 400;
  color: ${colors.defaultText};
  background-color: transparent;
  font-size: 14px;
  border: none;
  width: max-content;
  padding: 5px;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const AvailableDates = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const DisabledDates = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 6px;
  gap: 6px;
  font-size: 14px;
  margin-top: -4px;
  margin-bottom: 4px;

  & > div {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 6px;

    svg {
      display: block;
      width: 17px;
      height: 17px;
      fill: ${colors.defaultText};
    }

    button {
      padding: 6px;
      margin: 0;
      background-color: ${colors.primaryButton};
      border: 4px solid white;
      border-radius: 30px;

      svg {
        width: 10px;
        height: 10px;
        fill: white;
      }
    }
  }
`;

const FieldWithPadding = styled(Field)`
  margin-top: 16px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
  margin-bottom: 108px; // clear bottom footer
  gap: 24px;
`;

const SaveButton = styled.button`
  font-weight: 500;
  color: white;
  background-color: ${colors.primaryButton};
  font-size: 14px;
  border-radius: 4px;
  padding: 15px 50px;
  border: none;
  width: max-content;
  flex-shrink: 0;

  @media (max-width: ${tablet}) {
    padding: 12px 33px;
  }

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const WarningMessage = styled.div`
  font-weight: 500;
  font-size: 13px;
  flex-grow: 1;
  flex-shrink: 1;
  margin-bottom: 8px;
  padding: 16px;
  background-color: ${colors.lightBackground};
`;

const ErrorMessage = styled.div`
  color: ${colors.errorColor};
  font-weight: 500;
  font-size: 13px;
  flex-grow: 1;
  flex-shrink: 1;
`;

const ErrorHint = styled.span`
  display: inline-block;
  margin-left: 16px;
  color: ${colors.errorColor};
  font-weight: 400;
  font-size: 12px;
`;

const PaymentOption = styled.label`
  display: flex;
  /* flex-direction: column; */
  align-items: center;
  margin-bottom: 12px;
  margin-right: 6px;
  cursor: pointer;

  input {
    margin-right: 10px;
  }
`;

const PaymentOptionName = styled.span`
  font-size: 14px;
`;

const LabelBold = styled.span`
  font-weight: 500;
`;

const CancelPolity = styled.p`
  font-size: 12px;
  padding-bottom: 8px;
`;

const SundayRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.lightBorder};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const SundayText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;

  strong {
    font-weight: 500;
  }

  span {
    color: ${colors.fadedText};
    font-size: 13px;
  }
`;

const SundayRecord = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  color: ${colors.defaultText};
  text-decoration: underline;
`;

const SwitchButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  padding: 0;
  background-color: ${({ $on }) => ($on ? colors.primaryButton : colors.defaultBorder)};
  transition: background-color 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? '23px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: white;
    transition: left 0.2s;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DetailView = ({ isAdminMode, isEditMode, id, clearSelected }) => {
  const dispatch = useDispatch();
  const form = useSelector(getForm);
  const errors = useSelector(getFormErrors);
  const hasErrors = Object.keys(errors).length > 0;
  const hasGeoErrors = useSelector(getGeoErrors);
  const hasSaveError = useSelector(getSaveError);
  const uploadError = useSelector(getUploadError);
  const isSaving = useSelector(getIsSaving);
  const user = useSelector(getUser);
  const history = useHistory();

  // Billing B1.5: one Sunday choice for the host's whole account (docs/SUNDAYS.md).
  // It saves at once, for every venue of the host, also before a new venue is saved.
  const hostId = isAdminMode ? form.userId : user?.id;
  const { record: hostSundays, setRecord: setHostSundays, loading: sundaysLoading, error: sundaysReadError } = useHostSundays(hostId);
  const [sundayModal, setSundayModal] = useState(null); // 'agree' | 'view' | 'contact'
  const [sundaySaving, setSundaySaving] = useState(false);
  const [sundayError, setSundayError] = useState(false);
  const allSundaysOpen = useAllSundaysOpen();
  const sundayAccount = { sundays: hostSundays };
  const sundaysOpen = venueOpensSundays(sundayAccount, allSundaysOpen);
  const sundaysFollowAdmin = hostSundayChoice(sundayAccount) === null;
  const sundaysAgreed = sundaysOpen && !sundaysFollowAdmin;

  const saveSundays = async (action, contact) => {
    setSundayError(false);
    setSundaySaving(true);
    try {
      const next = await saveHostSundays({ hostId, action, current: hostSundays, contact, byUserId: user?.id });
      setHostSundays(next);
      setSundayModal(null);
    } catch (e) {
      console.error('Could not save the Sunday choice', e);
      setSundayError(true);
    }
    setSundaySaving(false);
  };

  const [showDatesModal, setDatesModal] = useState(false);

  // Venue-search VS1.3: the private record (on-site contact, requirements) and
  // the host requirements. The confirmation from /list-your-venue lives in this tab.
  const formRef = useRef(null);
  const [sessionConfirmation] = useState(readConfirmation);
  const [privateRecord, setPrivateRecordValue] = useState(null);
  const [privateLoaded, setPrivateLoaded] = useState(!isEditMode && !isAdminMode);
  const [requirementsChecked, setRequirementsChecked] = useState({});
  useEffect(() => {
    if (!(isEditMode || isAdminMode) || !hostId || !id) return;
    let active = true;
    getVenuePrivate(hostId, id)
      .then((record) => {
        if (!active) return;
        setPrivateRecordValue(record);
        dispatch(setPrivateRecord(record));
      })
      .catch((e) => console.error('Could not read the venue private record', e))
      .finally(() => active && setPrivateLoaded(true));
    return () => {
      active = false;
    };
  }, [isEditMode, isAdminMode, hostId, id]);
  const hasSavedRequirements = !!privateRecord?.intake?.requirements;
  const needsRequirements = !isAdminMode && privateLoaded && !hasSavedRequirements && !sessionConfirmation;
  const requirementsAllChecked = CURRENT_HOST_REQUIREMENTS.statements.every(({ id: statementId }) => requirementsChecked[statementId]);

  const uploadRef = useRef(null);
  const folderRef = useRef(null);
  const [showFolderLink] = useState(canPickFolder);
  const [mouse] = useState(hasMouse);

  // Photos on their way up: { id, file, status: 'working' | 'done' | 'error', progress, shrunk, preview, photo, unreadable }
  const [tiles, setTiles] = useState([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const [photoNotice, setPhotoNotice] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);
  const nextTileId = useRef(0);
  // Shrink 1 at a time (memory on phones), upload 3 at a time.
  const limiters = useRef(null);
  if (!limiters.current) limiters.current = { shrink: createLimiter(1), upload: createLimiter(UPLOAD_CONCURRENCY) };
  // Local copies of new photos, so a finished tile does not reload from the network.
  const previews = useRef({});

  const workingCount = tiles.filter((tile) => tile.status === 'working').length;
  const isUploading = workingCount > 0;
  const unreadableCount = tiles.filter((tile) => tile.unreadable).length;
  // Photos saved + in progress. Reading a .zip or folder takes time, so a pick can
  // finish after another one: each pick reserves its photos here at once.
  const photoCount = useRef(0);
  photoCount.current = form.photos.length + tiles.length;

  // build clean excluded dates arry; & remove any dates from the past
  const today = moment().format(DATE_FORMAT);
  const excludedDates = (Array.isArray(form.excludedDates) ? form.excludedDates : []).filter((date) => date > today);
  // build disabled dates for date-picker
  const disabledDates = excludedDates.map((date) => new Date(`${date}T00:00:00.000-06:00`));
  // group disabled dates by continuous dates
  const continuousDisabledDates = groupContinuousDates(excludedDates);

  const updateTile = (id, patch) => setTiles((current) => current.map((tile) => (tile.id === id ? { ...tile, ...patch } : tile)));

  const runTile = async ({ id, file, shrunk: alreadyShrunk }) => {
    try {
      let shrunk = alreadyShrunk;
      if (!shrunk) {
        shrunk = await limiters.current.shrink(async () => shrinkPhoto(await openSource(file)));
        updateTile(id, { shrunk, preview: URL.createObjectURL(shrunk) });
      }
      const photo = await limiters.current.upload(() => uploadPhoto(shrunk, (progress) => updateTile(id, { progress })));
      updateTile(id, { status: 'done', progress: 1, photo });
    } catch (error) {
      updateTile(id, { status: 'error', unreadable: isUnreadable(error) });
    }
  };

  const addFiles = async (files, options) => {
    if (!files || !files.length) return;
    const { sources, notes } = await collectSources(files, options);
    const plan = planPick(sources, photoCount.current);
    photoCount.current += plan.accepted.length;
    setPhotoNotice([...notes, pickMessage(plan)].filter(Boolean).join(' '));
    if (!plan.accepted.length) return;
    const newTiles = plan.accepted.map((file) => ({ id: nextTileId.current++, file, status: 'working', progress: 0 }));
    setBatchTotal((total) => (isUploading ? total + newTiles.length : newTiles.length));
    setTiles((current) => [...current, ...newTiles]);
    newTiles.forEach(runTile);
  };

  const retryTile = (tile) => {
    setBatchTotal((total) => (isUploading ? total + 1 : 1));
    updateTile(tile.id, { status: 'working', progress: 0 });
    runTile(tile);
  };

  const removeTile = (tile) => {
    if (tile.preview) URL.revokeObjectURL(tile.preview);
    setTiles((current) => current.filter((t) => t.id !== tile.id));
  };

  const onPhotoSelect = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = ''; // so the same file can be picked again
    addFiles(files);
  };

  const onFolderSelect = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    addFiles(files, { folder: true });
  };

  const dropZoneProps = {
    onDragEnter: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    },
    onDragOver: (e) => {
      if (hasFiles(e)) e.preventDefault();
    },
    onDragLeave: () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (!dragDepth.current) setIsDragging(false);
    },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      const entries = entriesFromDrop(e.dataTransfer);
      if (!entries.some((entry) => entry.isDirectory)) {
        addFiles([...e.dataTransfer.files]);
        return;
      }
      readDroppedEntries(entries)
        .then(({ files, truncated }) => addFiles(files, { folder: true, truncated }))
        .catch(() => setPhotoNotice('This folder could not be opened.'));
    }
  };

  // Finished photos join the venue in pick order.
  useEffect(() => {
    const commit = takeCommittable(tiles);
    if (!commit.length) return;
    commit.forEach((tile) => {
      previews.current[tile.photo.url] = tile.preview;
    });
    dispatch(addPhotos(commit.map((tile) => tile.photo)));
    const ids = new Set(commit.map((tile) => tile.id));
    setTiles((current) => current.filter((tile) => !ids.has(tile.id)));
  }, [tiles]);

  // A file dropped just outside the grid must not open in the tab and lose the form.
  useEffect(() => {
    const block = (e) => {
      if (hasFiles(e)) e.preventDefault();
    };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
      Object.values(previews.current).forEach((url) => url && URL.revokeObjectURL(url));
    };
  }, []);

  const photoTiles = {
    photos: form.photos,
    srcFor: (photo) => previews.current[photo.url] || photo.thumbnail || photo.url,
    onRemove: (photo) => dispatch(setFormValue({ photos: form.photos.filter((_photo) => _photo.url !== photo.url) }))
  };

  const onSaveClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setIsSaving(true));

    const props = JSON.parse(JSON.stringify(form));

    // clean data
    props.name = props.name.trim();
    props.description = props.description.trim();
    props.address_street = props.address_street.trim();
    props.address_city = props.address_city.trim();
    props.address_state = props.address_state.trim();
    props.address_zip = props.address_zip.trim();
    props.additionalNotes = props.additionalNotes.trim();
    props.dayPrice = formatIntString(props.dayPrice);
    props.photos = props.photos.filter((photo) => photo.url);
    props.disabled = props.disabled === true;
    props.allowPayAnotherAmount = Boolean(props.allowPayAnotherAmount);
    props.porcentageAmountToPayCustom = formatIntString(props.porcentageAmountToPayCustom);

    delete props.verified; // `verified` is only used for form logic, don't change value

    // validate: today's fields, then the field dictionary's rules (VS1.3)
    const requiredFields = ['name', 'description', 'setting', 'venueType', 'address_street', 'address_city', 'address_state', 'address_zip', 'dayPrice'];
    const errors = requiredFields.reduce((errors, field) => {
      if (!props[field]) errors[field] = true;
      return errors;
    }, {});
    Object.assign(errors, venueProblems({ ...form, photos: props.photos }, { needsRequirements, requirementsChecked: requirementsAllChecked }));
    if (errors.photos) {
      dispatch(setUploadError(editorCopy.photosMinimum(MIN_PHOTOS)));
    }
    if (Object.keys(errors).length > 0) {
      dispatch(setFormErrors(errors));
      window.requestAnimationFrame(() => {
        const first = formRef.current && formRef.current.querySelector('[data-invalid="true"]');
        if (first && first.scrollIntoView) first.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      return;
    }

    // Old fields, filled from the new facts until search reads them (Phase 2).
    const values = ruleValues(form);
    props.maxGuests = maxGuestsFrom(values);
    props.amenities = amenitiesFrom(values, form.amenities);
    ['facts', 'estimated', 'eventTypesRestricted', 'contactName', 'contactPhone'].forEach((key) => delete props[key]);
    props.address_country = 'US'; // ISO code (DS-G2)

    props.address = {
      street: props.address_street,
      city: props.address_city,
      state: props.address_state,
      zip: props.address_zip,
      country: props.address_country
    };
    delete props.address_street;
    delete props.address_city;
    delete props.address_state;
    delete props.address_zip;
    delete props.address_country;

    try {
      const [lat, lng, exactMatch] = await geoCode(
        encodeURIComponent(`${props.address.street}, ${props.address.city}, ${props.address.state}, ${props.address.country}`)
      );

      if (!exactMatch) {
        dispatch(setGeoErrors(true));
        return;
      }
      props.location = { lat, lng };
    } catch (e) {
      dispatch(setGeoErrors(true));
      return;
    }

    // Billing B1.5: the Sunday switch saves `sundays` on its own, for all the host's venues.
    delete props.sundays;
    delete props.userId;

    const isNew = !(isEditMode || isAdminMode);
    if (isNew) {
      Object.assign(props, {
        verified: false,
        userId: user.id,
        // Venue records are public. Never put the host's name, email or phone
        // here: they stay in the private users record (security S3.3, S3.3b).
        host: {
          type: 'Individual'
        },
        ...(hostSundayChoice({ sundays: hostSundays }) === null ? {} : { sundays: venueSundaysFlag(hostSundays.open) })
      });
    }
    const requirementsVersion = isAdminMode || hasSavedRequirements ? null : sessionConfirmation?.version || CURRENT_HOST_REQUIREMENTS.version;
    try {
      await saveHostedVenue({
        id: isNew ? null : id,
        hostId,
        buildProps: ({ create, remove }) => ({ ...props, ...venueFactsWrite(form, { create, remove }) }),
        buildPrivate: ({ now, role }) => privateWrite(form, { requirementsVersion, userId: user.id, now, role })
      });
    } catch (e) {
      console.error('Could not save the venue', e);
      dispatch(setSaveError(true));
      return;
    }
    if (isNew) {
      useHostVenueSaveTag({ label: 'Save New Venue', hostId: user.id, hostName: user.firstName + ' ' + user.lastName });
      localStorage.removeItem('hosted_add_venue_started');
      clearConfirmation();
    } else {
      useHostVenueEditTag({ label: 'Edit Venue', hostId: user.id, venuName: props.name, hostName: user.firstName + ' ' + user.lastName });
    }
    window.location.reload(); // refetch our newest venues
  };

  const onCancelSave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const hasStarted = localStorage.getItem('hosted_add_venue_started');

    useHostVenueCancelTag({ label: 'cancel add venue', hostId: user.id, hostName: user.firstName + ' ' + user.lastName });

    if (hasStarted) {
      localStorage.removeItem('hosted_add_venue_started');
    }

    if (clearSelected) {
      clearSelected();
    } else {
      history.push('/');
    }
  };

  useEffect(() => {
    const unlisten = history.listen(() => {
      const hasStarted = localStorage.getItem('hosted_add_venue_started');
      if (hasStarted) {
        useHostVenueNavigateAwayTag({ label: 'navigate_away_add_venue', userId: user.id, userName: user.firstName + ' ' + user.lastName });
        localStorage.removeItem('hosted_add_venue_started');
      }
    });

    return () => {
      unlisten();
    };
  }, [history]);

  return (
    <FormContent ref={formRef}>
      <FormTitle>
        <VenueIcon />
        <FormTitleText>{isAdminMode ? 'Review' : isEditMode ? 'Edit' : 'Add New'} Venue</FormTitleText>
        <CloseButtonWrapper>
          <button onClick={onCancelSave}>
            <CloseIcon />
          </button>
        </CloseButtonWrapper>
      </FormTitle>
      <Title>Venue Photos</Title>
      {form.photos.length >= 2 && <ReorderHint>{mouse ? 'Drag to reorder.' : 'Press and hold a photo to move it.'} The first photo is the cover.</ReorderHint>}
      <PhotoDropZone {...dropZoneProps}>
        <Photos>
          <Suspense fallback={<StaticPhotos {...photoTiles} />}>
            <SortablePhotos {...photoTiles} onMove={(from, to) => dispatch(movePhoto({ from, to }))} />
          </Suspense>
          {tiles.map((tile) => (
            <UploadTile key={`upload-${tile.id}`} className={tile.status}>
              {tile.status === 'error' && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeTile(tile);
                  }}
                >
                  <WhiteCloseIcon />
                </button>
              )}
              {tile.preview && <img src={tile.preview} alt="" />}
              {tile.status === 'working' && <ProgressRing progress={tile.progress} />}
              {tile.status === 'error' && (
                <div className="tile-error">
                  <span>Upload failed</span>
                  {!tile.unreadable && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        retryTile(tile);
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}
            </UploadTile>
          ))}
          {form.photos.length + tiles.length < MIN_PHOTOS &&
            Array.from(Array(MIN_PHOTOS - form.photos.length - tiles.length)).map((_, i) => (
              <div key={i} className="empty-photo">
                <ImagesIcon />
              </div>
            ))}
        </Photos>
        {isDragging && <div className="drop-hint">Drop photos, a folder or a .zip file here</div>}
      </PhotoDropZone>
      <input ref={uploadRef} type="file" accept="image/*,.heic,.heif,.zip,application/zip" multiple style={{ display: 'none' }} onChange={onPhotoSelect} />
      {showFolderLink && <input ref={folderRef} type="file" webkitdirectory="" multiple style={{ display: 'none' }} onChange={onFolderSelect} />}
      <FormButtonWrapper>
        <InputButton
          hasError={errors.photos}
          onClick={() => {
            uploadRef.current.click();
          }}
          disabled={isUploading}
        >
          {isUploading ? `Uploading ${Math.max(1, batchTotal - workingCount + 1)} of ${batchTotal}…` : 'Add Photos'}
        </InputButton>
        {uploadError && <ErrorHint>{uploadError}</ErrorHint>}
      </FormButtonWrapper>
      <PhotoHint>
        Photos or a .zip file.
        {showFolderLink && (
          <>
            {' '}
            To add a whole folder, drag it here or{' '}
            <button type="button" onClick={() => folderRef.current.click()}>
              choose a folder
            </button>
            .
          </>
        )}
      </PhotoHint>
      {photoNotice && <PhotoNotice role="status">{photoNotice}</PhotoNotice>}
      {unreadableCount > 0 && <PhotoNotice role="status">{unreadableMessage(unreadableCount)}</PhotoNotice>}

      <Title>Venue Details</Title>
      <Field hasError={errors.name} data-invalid={!!errors.name}>
        <FormLabel>Title</FormLabel>
        <Input placeholder="Venue name" value={form.name} onChange={(e) => dispatch(setFormValue({ name: e.target.value }))} />
        <TooLong name="name" id="title" form={form} errors={errors} />
      </Field>
      <Field hasError={errors.description} data-invalid={!!errors.description}>
        <FormLabel>Description</FormLabel>
        <FieldHelp>{DESCRIPTION_HELP}</FieldHelp>
        <VenueTextField
          label="Description"
          placeholder={DESCRIPTION_PLACEHOLDER}
          minLines={4}
          value={form.description}
          onChange={(description) => dispatch(setFormValue({ description }))}
        />
        <TooLong name="description" id="description" form={form} errors={errors} />
      </Field>
      <FieldGroup>
        <Field hasError={errors.setting}>
          <Label id="setting" />
          <Select
            value={form.setting}
            onChange={(e) => {
              dispatch(setFormValue({ setting: e.target.value }));
            }}
          >
            {legacyOptions('setting', form.setting).map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field hasError={errors.venueType}>
          <FormLabel>{editorCopy.venueType}</FormLabel>
          <Select
            value={form.venueType}
            onChange={(e) => {
              dispatch(setFormValue({ venueType: e.target.value }));
            }}
          >
            {legacyOptions('venueType', form.venueType, ['other']).map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
      </FieldGroup>

      <Title>Venue Address</Title>
      <Field hasError={errors.address_street}>
        <FormLabel>Street</FormLabel>
        <Input placeholder="100 W Park St" value={form.address_street} onChange={(e) => dispatch(setFormValue({ address_street: e.target.value }))} />
      </Field>
      <FieldGroup>
        <Field hasError={errors.address_city}>
          <FormLabel>City</FormLabel>
          <Input placeholder="Salt Lake City" value={form.address_city} onChange={(e) => dispatch(setFormValue({ address_city: e.target.value }))} />
        </Field>
        <Field hasError={errors.address_state}>
          <FormLabel>State</FormLabel>
          <Select
            value={form.address_state}
            onChange={(e) => {
              dispatch(setFormValue({ address_state: e.target.value }));
            }}
          >
            <option value=""></option>
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </Select>
        </Field>
        <Field hasError={errors.address_zip}>
          <FormLabel>Zip Code</FormLabel>
          <Input placeholder="00000" value={form.address_zip} onChange={(e) => dispatch(setFormValue({ address_zip: e.target.value }))} />
        </Field>
      </FieldGroup>
      <VenueFactSections form={form} errors={errors} />

      <Title style={{ marginTop: 24 }}>Pricing Details</Title>
      <FieldGroup>
        <Field hasError={errors.dayPrice} data-invalid={!!errors.dayPrice}>
          <Label id="fullDay.price" />
          <Input
            placeholder="$1,000"
            value={form.dayPrice}
            onChange={(e) => {
              dispatch(setFormValue({ dayPrice: e.target.value }));
            }}
          />
        </Field>
      </FieldGroup>
      <DayHours form={form} errors={errors} />
      <HourlyPrice form={form} errors={errors} currency={currencyFor(form.address_country)} />
      <Title>Choose how you'd like to be paid</Title>
      <FieldGroupFullWidth>
        <PaymentOption>
          <input type="checkbox" checked={true} disabled />
          <PaymentOptionName>
            <LabelBold>Full Payment Upfront</LabelBold> – Guests pay 100% when booking.
          </PaymentOptionName>
        </PaymentOption>
        <PaymentOption>
          <input
            type="checkbox"
            checked={form.allowPayAnotherAmount}
            onChange={(e) => {
              const checked = e.target.checked;
              dispatch(setFormValue({ allowPayAnotherAmount: checked, porcentageAmountToPayCustom: checked ? 50 : undefined }));
            }}
          />
          <PaymentOptionName>
            <LabelBold>Split Payment</LabelBold> – Guests pay 50% now and 50% later (3 days before the event).
          </PaymentOptionName>
        </PaymentOption>

        {form.allowPayAnotherAmount && (
          <div className="policy-box">
            <CancelPolity>
              <strong>Cancellation Policy:</strong> The initial 50% payment is <strong>non-refundable</strong>. The remaining 50% is due 3 days before the
              event.
            </CancelPolity>
          </div>
        )}
      </FieldGroupFullWidth>

      <Title>Availability</Title>
      <AvailableDates>
        {form.disabled ? (
          <WarningMessage>Your venue will not appear in search or receive new reservations.</WarningMessage>
        ) : (
          <>
            <FormLabel>
              Select any dates your venue is <strong>unavailable</strong> (optional)
            </FormLabel>
            {continuousDisabledDates.length > 0 && (
              <DisabledDates>
                {continuousDisabledDates.map((group, i) => {
                  if (!group || !group.length) {
                    return null;
                  }
                  const button = (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        dispatch(
                          setFormValue({
                            excludedDates: excludedDates.filter((date) => !group.includes(date))
                          })
                        );
                      }}
                    >
                      <WhiteCloseIcon />
                    </button>
                  );
                  if (group.length === 1) {
                    return (
                      <div key={i}>
                        {button}
                        <CalendarIcon />
                        <div>{moment(`${group[0]}T00:00:00.000`).format(END_DATE_FORMAT)}</div>
                      </div>
                    );
                  } else {
                    return (
                      <div key={i}>
                        {button}
                        <CalendarIcon />
                        <div>
                          {moment(`${group[0]}T00:00:00.000`).format(START_DATE_FORMAT)} &ndash;{' '}
                          {moment(`${group[group.length - 1]}T00:00:00.000`).format(END_DATE_FORMAT)}
                        </div>
                      </div>
                    );
                  }
                })}
              </DisabledDates>
            )}
          </>
        )}
        <FormButtonWrapper>
          <InputButton
            onClick={() => {
              useHostVenueAddUnavailableTag({ label: 'Add Unavailable Dates', hostId: user.id, hostName: user.firstName + ' ' + user.lastName });
              setDatesModal(true);
            }}
            disabled={form.disabled}
            ghostStyle={form.disabled}
          >
            Add Unavailable Dates
          </InputButton>
          {(!!form.disabled || !!form.verified) && (
            <InputButton
              onClick={() => {
                useHostVenueSwitchTag({
                  label: form.disabled ? 'Resume Venue' : 'Pause Venue',
                  hostId: user.id,
                  hostName: user.firstName + ' ' + user.lastName
                });
                dispatch(setFormValue({ disabled: !form.disabled }));
              }}
              ghostStyle={!form.disabled}
            >
              {form.disabled ? 'Resume Venue' : 'Pause Venue'}
            </InputButton>
          )}
        </FormButtonWrapper>
      </AvailableDates>
      {/* B1.5b: hidden until early 2027 (HOST_SUNDAY_SWITCH in sundays.js) */}
      {HOST_SUNDAY_SWITCH && (
        <SundayRow>
          <SundayText>
            <strong>Open Sundays for booking</strong>
            <span>Yedding's team does not work on Sundays. If you open Sundays, you run the event day yourself. This covers all your venues.</span>
            {sundaysOpen && sundaysFollowAdmin && <span>Yedding has opened Sundays for all venues for now.</span>}
            {sundaysAgreed && (
              <SundayRecord>
                <span>
                  You agreed to Sunday Terms version {hostSundays.termsVersion} on {moment(hostSundays.acceptedAt).format('MMM D, YYYY, h:mm a')}.{' '}
                  <LinkButton type="button" onClick={() => setSundayModal('view')}>
                    View terms
                  </LinkButton>
                </span>
                {hostSundays.contact && (
                  <span>
                    On-site contact: {hostSundays.contact.name}, {formatPhone(hostSundays.contact.phone)}.{' '}
                    <LinkButton type="button" onClick={() => setSundayModal('contact')}>
                      Change
                    </LinkButton>
                  </span>
                )}
              </SundayRecord>
            )}
            {sundaysReadError && <ErrorMessage>We could not load your Sunday choice. Please reload the page.</ErrorMessage>}
            {sundayError && !sundayModal && <ErrorMessage>We could not save this. Please try again.</ErrorMessage>}
          </SundayText>
          <SwitchButton
            type="button"
            role="switch"
            aria-checked={sundaysOpen}
            aria-label="Open Sundays for booking"
            $on={sundaysOpen}
            disabled={sundaySaving || sundaysLoading || sundaysReadError || !hostId}
            onClick={(e) => {
              e.preventDefault();
              if (sundaysOpen) {
                saveSundays('close');
              } else {
                setSundayError(false);
                setSundayModal('agree');
              }
            }}
          />
        </SundayRow>
      )}
      {HOST_SUNDAY_SWITCH && sundayModal && (
        <SundayTermsModal
          mode={sundayModal}
          version={sundayModal === 'view' ? hostSundays?.termsVersion : undefined}
          initialContact={hostSundays?.contact}
          saving={sundaySaving}
          error={sundayError}
          onCancel={() => {
            setSundayModal(null);
            setSundayError(false);
          }}
          onSubmit={(contact) => saveSundays(sundayModal === 'agree' ? 'open' : 'contact', contact)}
        />
      )}
      {showDatesModal && (
        <ExcludeDatesModal
          onCancel={() => setDatesModal(false)}
          onPick={(dateRange) => {
            // determine new excluded dates
            const { startDate, endDate } = dateRange;
            const end = moment(endDate);
            const start = moment(startDate);
            const nextRange = [start.format(DATE_FORMAT)];
            let next = start;
            while (next.valueOf() !== end.valueOf()) {
              next = moment(next).add(1, 'day');
              nextRange.push(next.format(DATE_FORMAT));
            }
            let nextExcludedDates = [...excludedDates, ...nextRange];
            // sort in reverse order to speed up search
            nextExcludedDates.sort((a, b) => {
              if (a < b) return 1;
              if (b < a) return -1;
              return 0;
            });
            // remove duplicates
            nextExcludedDates = nextExcludedDates.filter((value, i, arr) => i === 0 || value !== arr[i - 1]);
            // save
            dispatch(setFormValue({ excludedDates: nextExcludedDates }));
            setDatesModal(false);
          }}
          disabledDates={disabledDates}
        />
      )}

      <OnSiteContact form={form} errors={errors} />

      <FieldWithPadding hasError={errors.additionalNotes} data-invalid={!!errors.additionalNotes}>
        <FormLabel>Additional Notes</FormLabel>
        <VenueTextField
          label="Additional Notes"
          placeholder={NOTES_PLACEHOLDER}
          minLines={3}
          value={form.additionalNotes}
          onChange={(additionalNotes) => dispatch(setFormValue({ additionalNotes }))}
        />
        <TooLong name="additionalNotes" id="additionalNotes" form={form} errors={errors} />
      </FieldWithPadding>

      {needsRequirements && (
        <RequirementsBox checked={requirementsChecked} onChange={setRequirementsChecked} error={!!errors.requirements && !requirementsAllChecked} />
      )}

      <ButtonWrapper>
        {hasErrors && <ErrorMessage>{editorCopy.fillRequired}</ErrorMessage>}
        {hasGeoErrors && <ErrorMessage>We could not locate the venue from the provided address.</ErrorMessage>}
        {hasSaveError && <ErrorMessage>Failed to save venue. Please try again.</ErrorMessage>}
        {isUploading && <ErrorMessage>Wait for photos to finish uploading.</ErrorMessage>}
        <TextButton onClick={onCancelSave}>Cancel</TextButton>
        <SaveButton onClick={onSaveClick} disabled={isSaving || isUploading}>
          {isSaving ? 'Saving...' : 'Save Venue'}
        </SaveButton>
      </ButtonWrapper>
    </FormContent>
  );
};

export default DetailView;
